import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  cancelIllustrationRun,
  getIllustrationRun,
  startIllustrationRun,
  type IllustrationRun,
  type RunEstimate,
} from "../api";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import { runLine, type RunLine } from "./runprogress";

/**
 * IllustrateChapter is the whole-chapter run (ADR-0074, requirement 0031):
 * one call chooses where the pictures go, then each is drawn in turn.
 *
 * It sits beside the per-paragraph **+** and does not replace it. The author
 * still knows best where one particular picture belongs; what they cannot do
 * on a phone is make that decision eight times, and the model has just read
 * the chapter.
 *
 * Folded until asked for, and priced before it is tapped: a run is the largest
 * single spend an author can start with one press outside narration.
 */
export default function IllustrateChapter({
  chapterID,
  bookID,
  estimates,
  canDraw,
  onDrawn,
}: {
  chapterID: string;
  bookID: string;
  /** What each run length costs, priced by the server. */
  estimates?: RunEstimate[];
  /** Whether the cast has been read, which a picture needs (ADR-0039). */
  canDraw: boolean;
  /** Called when a run places pictures, so the chapter reloads them. */
  onDrawn: () => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [spots, setSpots] = useState(4);

  // Polled only while a run is live, and the poll is what refreshes the
  // chapter's pictures: they arrive placed, one at a time.
  const run = useQuery({
    queryKey: ["illustration-run", chapterID],
    queryFn: async () => {
      const answer = await getIllustrationRun(chapterID);
      return answer.run;
    },
    refetchInterval: (query) => (live(query.state.data ?? null) ? 3000 : false),
    retry: false,
  });
  const current = run.data ?? null;
  const running = live(current);

  const start = useMutation({
    mutationFn: () => startIllustrationRun(chapterID, spots),
    onSuccess: (started) => {
      run.refetch().catch(() => {});
      setOpen(false);
      void started;
    },
  });

  // A picture arriving is a picture placed, so the chapter has to be re-read.
  const [seen, setSeen] = useState(0);
  if (current && current.done !== seen) {
    setSeen(current.done);
    onDrawn();
  }

  const chosen = estimates?.find((e) => e.spots === spots);

  if (running && current) {
    return <Banner run={current} bookID={bookID} chapterID={chapterID} />;
  }

  return (
    <section data-testid="illustrate-chapter" className="mb-4">
      {current && <Banner run={current} bookID={bookID} chapterID={chapterID} />}
      <button
        type="button"
        data-testid="illustrate-open"
        aria-expanded={open}
        disabled={!canDraw}
        onClick={() => setOpen((was) => !was)}
        className="w-full rounded-control border border-dashed border-line-strong px-3 py-2.5 text-sm text-ink-soft transition-colors hover:border-sage hover:text-ink disabled:opacity-40"
      >
        {t.images.illustrateChapter}
      </button>

      {open && (
        <div className="mt-2 rounded-card border border-line bg-surface p-3">
          <p className="text-xs leading-5 text-muted">{t.images.illustrateWhy}</p>
          <h4 className="mt-3 mb-2 text-xs font-medium text-ink-soft">
            {t.images.howManyPictures}
          </h4>
          {/* Chips, never a menu: a number on a phone is a thumb's target,
              and a native <select> hides every option behind a tap. */}
          <div role="group" aria-label={t.images.howManyPictures} className="flex flex-wrap gap-2">
            {(estimates ?? []).map((estimate) => (
              <button
                key={estimate.spots}
                type="button"
                aria-pressed={spots === estimate.spots}
                data-testid={`illustrate-spots-${estimate.spots}`}
                onClick={() => setSpots(estimate.spots)}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  spots === estimate.spots
                    ? "border-sage bg-sage-soft font-medium text-sage-deep"
                    : "border-line text-ink-soft hover:bg-sunk"
                }`}
              >
                {estimate.spots}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="illustrate-start"
            disabled={start.isPending}
            onClick={() => start.mutate()}
            className="mt-3 w-full rounded-control bg-sage px-4 py-2.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
          >
            {start.isPending ? t.images.illustrateStarting : t.images.illustrateStart}
            {chosen && (
              <span data-testid="illustrate-price" className="font-normal opacity-80">
                {t.book.sep}
                <bdi dir="ltr">{chosen.display}</bdi>
              </span>
            )}
          </button>
          <MutationError error={start.error} fallback={t.images.illustrateFailed} />
        </div>
      )}
    </section>
  );
}

function live(run: IllustrationRun | null): boolean {
  return run?.status === "planning" || run?.status === "drawing";
}

/** The run's line in the reader's language. The choosing is `runLine`'s. */
function sentence(line: RunLine, t: ReturnType<typeof useT>): string {
  switch (line.kind) {
    case "planning":
      return t.images.runPlanning;
    case "drawing":
      return t.images.runDrawing(line.at, line.of);
    case "drawn":
      return t.images.runDrawn(line.drawn, line.planned);
    case "short":
      return t.images.runShort(line.drawn, line.planned, line.wanted);
    case "stopped":
      return t.images.runStopped(line.drawn);
    case "none":
      return t.images.runNone;
  }
}

/**
 * Banner is how a run stands: what it drew of what it planned, and what went
 * wrong with any spot, in the studio's own words rather than the provider's.
 *
 * The sentence comes from `runLine`, which reads the run's `status` (ADR-0085).
 * It must not be derived here by comparing counts: a run that planned fewer
 * spots than were asked for is finished while `done < wanted`, and the banner
 * used to call that "Drawing 8 of 8…" for ever.
 */
function Banner({
  run,
  bookID,
  chapterID,
}: {
  run: IllustrationRun;
  bookID: string;
  chapterID: string;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  // Stop is on the live run and nowhere else (ADR-0167): the run was priced
  // and started with one press, and until this it could only be waited out.
  // The answer is the run as it now stands, so the banner settles without
  // waiting for the next poll.
  const stop = useMutation({
    mutationFn: () => cancelIllustrationRun(chapterID),
    onSuccess: (answer) => {
      queryClient.setQueryData(["illustration-run", chapterID], answer.run);
    },
  });
  const failed = run.spots.filter((s) => s.status === "failed");
  const running = live(run);
  return (
    <div
      data-testid="illustrate-banner"
      data-status={run.status}
      aria-live="polite"
      className={`mb-3 rounded-card border p-3 text-sm ${
        run.status === "failed"
          ? "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30"
          : "border-line bg-sunk"
      }`}
    >
      <p className="flex items-center gap-2 font-medium">
        {running && (
          <span className="size-2.5 animate-pulse rounded-full bg-sage" aria-hidden="true" />
        )}
        <span className="min-w-0 flex-1">{sentence(runLine(run), t)}</span>
        {running && (
          <button
            type="button"
            data-testid="illustrate-stop"
            disabled={stop.isPending}
            onClick={() => stop.mutate()}
            className="shrink-0 rounded-control border border-line-strong px-3 py-1.5 text-sm font-normal text-ink-soft transition-colors hover:bg-surface disabled:opacity-40"
          >
            {stop.isPending ? t.images.stoppingRun : t.images.stopRun}
          </button>
        )}
      </p>
      <MutationError error={stop.error} fallback={t.images.stopRunFailed} />
      {run.status === "failed" && run.reason && (
        <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
          {t.images.runReason(run.reason)}
        </p>
      )}
      {failed.length > 0 && (
        <ul className="mt-1.5 space-y-1 text-xs text-muted">
          {failed.map((spot) => (
            <li key={spot.position} data-testid="illustrate-spot-failed">
              {spot.reason === "needs_face" && spot.who?.length ? (
                <>
                  {t.portraits.castMissing(spot.who)}{" "}
                  <Link
                    to={`/studio/books/${bookID}/characters`}
                    className="text-sage-deep underline underline-offset-2"
                  >
                    {t.portraits.goToCast}
                  </Link>
                </>
              ) : (
                t.images.runReason(spot.reason ?? "")
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
